<script>
import {
    armUnit,
    disarmUnit,
    setManagers,
    setPowerMode,
    setCheckinTimes,
    setShorePowerLatch,
    setCellularCarrier,
    setCanConfig,
    getFirmwareUpdateList,
    getOTASAS,
    setPendingFirmware,
    sendSMSWake,
    setOrganizations,
} from '../../../../api/units';

import { mapGetters } from 'vuex';

import toastr from 'toastr';
import { STATUS_ERROR } from '../../../../api/client';
import Button from '../../../../controls/Button.vue';
import IntervalSlider from './IntervalSlider.vue';
import {
    getOrganizations,
    getOrganizationUser,
} from '../../../../api/organizations';

export default {
    components: { Button },
    name: 'UnitAdministration',

    props: {
        unit: Object,
        powerMode: String,
        snapCollapsibles: Boolean,
        unitState: String,
        pendingArmState: String,
        pendingPowerMode: String,
        normalCheckinIntervalSeconds: Number,
        fenceCheckinIntervalSeconds: Number,
        shorePowerLatch: Boolean,
        pending_can_config: String,
    },

    data() {
        return {
            armStateOptions: [
                {
                    label: this.$t('noOverride'),
                    value: 'ARM_STATE_NONE',
                },
                {
                    label: this.$t('disarmed'),
                    value: 'ARM_STATE_DISARMED',
                },
                {
                    label: this.$t('armed'),
                    value: 'ARM_STATE_ARMED',
                },
            ],
            collapsiblesOpen: {
                stateOverrides: false,
                unitManagers: false,
                cellularCarriers: false,
            },
            managers: [],
            desiredState: '',
            desiredPowerMode: '',
            edits: false, // Flagged whenever changes to managers are made
            selectedState: '',
            selectedPowerMode: '',
            showPowerModeConfirm: false,
            showStateChangeConfirm: false,
            selectedAddUser: { invalid: true },
            intervalSections: [
                {
                    interval: 1,
                    unit: 'continuous',
                    start: 1,
                    end: 1,
                    doNotMarkEnd: true,
                },
                {
                    interval: 5,
                    unit: 'minute',
                    start: 5,
                    end: 55,
                    weight: 8,
                },
                {
                    interval: 0.5,
                    unit: 'hour',
                    start: 1,
                    end: 1.5,
                    weight: 2,
                    doNotMarkEnd: true,
                },
                {
                    interval: 1,
                    unit: 'hour',
                    start: 2,
                    end: 36,
                    weight: 16,
                },
                {
                    interval: 1,
                    unit: 'day',
                    start: 2,
                    end: 10,
                    weight: 4,
                },
                {
                    interval: 1,
                    unit: 'week',
                    start: 1,
                    end: 4,
                    weight: 2,
                },
            ],
            normalIntervalIntermediateValue: null, // Of form: { value: Number, unit: String },
            fenceIntervalIntermediateValue: null, // '''
            desiredNormalIntervalValue: {}, // '''
            desiredFenceIntervalValue: {}, // '''
            showNormalIntervalChangeConfirm: false,
            showFenceIntervalChangeConfirm: false,
            fenceCheckinModified: false,
            normalCheckinModified: false,
            selectedCellularCarrier: '',
            showCellularCarrierChangeConfirm: false,
            showSmsWakeConfirm: false,
            showViewEditConfiguration: false,
            desiredCellularCarrier: '',
            jsonEditFile: null,
            jsonValid: false,
            statusErrorText: '',
            statusErrorDisplayText: '',
            showFirmwareSelection: false,
            selectedFirmwareOption: '',
            firmwareArray: [],
            unitOrganizations: [],
            selectedOrganizaiton: {},
            selectedAddOrganization: { invalid: true },
            isAdminOrg: {},
            isOrgManager: {},
        };
    },

    computed: {
        ...mapGetters(['isUserAdmin', 'isOrganizationAdmin']),

        armStateToggleValue() {
            if (this.pendingArmState) {
                return this.pendingArmState;
            } else if (
                this.desiredState !== this.unitState &&
                this.desiredState !== 'ARM_STATE_NONE'
            ) {
                return this.desiredState;
            }

            if (this.unitState === 'ARM_STATE_ARMED') {
                return this.unitState;
            } else {
                return 'ARM_STATE_DISARMED';
            }
        },

        powerModeToggleValue() {
            if (this.pendingPowerMode) {
                return this.pendingPowerMode;
            } else if (
                this.desiredPowerMode !== this.powerMode &&
                this.desiredPowerMode !== 'POWER_MODE_NONE'
            ) {
                return this.desiredPowerMode;
            }

            if (this.powerMode === 'POWER_MODE_SETUP') {
                return this.powerMode;
            } else {
                // As far as a the toggle switch in the UI is concerned this is a binary value, even though power may be something else
                return 'POWER_MODE_HIGH';
            }
        },

        selectedAddUserValidManager() {
            if (this.selectedAddUser) {
                // Check to see if this user is already a manager
                if (this.managers) {
                    for (const manager of this.managers) {
                        if (this.selectedAddUser.user_id === manager.user_id) {
                            return false;
                        }
                    }
                }

                return true;
            }
            return false;
        },

        selectedAddOrganizationValidManager() {
            if (this.selectedAddOrganization) {
                // Check to see if this organization is already added
                if (this.unitOrganizations) {
                    for (const organization of this.unitOrganizations) {
                        if (
                            this.selectedAddOrganization.organization_id ===
                            organization.id
                        ) {
                            return false;
                        }
                    }
                }

                return true;
            }
            return false;
        },

        usersList() {
            return this.$store.state.userList;
        },

        alphabeticalUsers() {
            return this.usersList
                .slice()
                .sort(this.$store.getters.intlCompareByName);
        },

        userOptions() {
            const users = this.alphabeticalUsers;
            if (users && users.length > 0) {
                const options = users.reduce((options, user) => {
                    options.push({
                        label: user.name,
                        value: user.id,
                    });

                    return options;
                }, []);
                return options;
            }
            return [];
        },

        cellularCarrierList() {
            return this.$store.state.cellularCarriers;
        },

        cellularCarrierOptions() {
            const cellularCarriers = this.cellularCarrierList;
            if (cellularCarriers && cellularCarriers.length > 0) {
                const options = cellularCarriers.reduce(
                    (options, cellularCarrier) => {
                        options.push({
                            label: cellularCarrier.name,
                            value: cellularCarrier.value,
                        });
                        return options;
                    },
                    [],
                );
                return options;
            }
            return [];
        },

        organizations() {
            return this.$store.state.organizations;
        },

        organizationOptions() {
            return this.organizations.map((org) => ({
                label: org.organization_name,
                value: Number(org.id),
            }));
        },

        // sorts the organizations alphabetically by label for the organizations dropdown menu display
        sortedOrganizations() {
            if (this.organizationOptions.length > 0) {
                return this.organizationOptions.slice().sort((a, b) => {
                    return a.label.localeCompare(b.label);
                });
            }
            return [];
        },

        selectedFenceIntervalLabel() {
            let val;
            if (this.fenceIntervalIntermediateValue) {
                val = this.fenceIntervalIntermediateValue;
            } else if (this.desiredFenceIntervalValue) {
                val = this.desiredFenceIntervalValue;
            }

            return this.getIntervalLabelFromValue(val);
        },

        selectedNormalIntervalLabel() {
            let val;
            if (this.normalIntervalIntermediateValue) {
                val = this.normalIntervalIntermediateValue;
            } else if (this.desiredNormalIntervalValue) {
                val = this.desiredNormalIntervalValue;
            }

            return this.getIntervalLabelFromValue(val);
        },

        normalIntervalSliderValue() {
            if (this.normalIntervalIntermediateValue) {
                return this.normalIntervalIntermediateValue;
            }
            return this.desiredNormalIntervalValue;
        },

        fenceIntervalSliderValue() {
            if (this.fenceIntervalIntermediateValue) {
                return this.fenceIntervalIntermediateValue;
            }
            return this.desiredFenceIntervalValue;
        },

        selectedCellularCarrierLabel() {
            return this.desiredCellularCarrier.name;
        },

        backendCellularCarrierName() {
            return (
                this.unit?.cellular_carrier ||
                this.mainBoardFullCheckin?.CellularCarrier ||
                this.mainBoardFullCheckin?.cellular_carrier ||
                this.unit?.last_full_checkin?.message?.high?.modem_board_data
                    ?.cell?.carrier ||
                ''
            );
        },

        cellularCarrierOptionValue() {
            // catch if a selection change has been made
            if (this.selectedCellularCarrier) {
                return this.selectedCellularCarrier.value;
            }

            const selectedCarrierOption = this.cellularCarrierList.find(
                (item) => item.name === this.backendCellularCarrierName,
            );

            // If no valid backend carrier is present, treat as unassigned.
            return selectedCarrierOption ? selectedCarrierOption.value : 0;
        },

        firmwareOptions() {
            return this.firmwareArray;
        },

        firmwareOptionValue() {
            return this.selectedFirmwareOption;
        },

        mainBoardFullCheckin() {
            return (
                this.unit?.last_full_checkin?.message?.high?.main_board_data ??
                {}
            );
        },

        jsonFile() {
            if (
                this.unit.pending_can_config !== null &&
                this.unit.pending_can_config !== undefined
            ) {
                return JSON.stringify(this.unit.pending_can_config, null, 4);
            }
            if (
                this.unit.can_config !== null &&
                this.unit.can_config !== undefined
            ) {
                return JSON.stringify(this.unit.can_config, null, 4);
            }

            return '{}';
        },
    },

    watch: {
        fenceCheckinIntervalSeconds: {
            immediate: true,
            handler: function (newVal, oldVal) {
                if (
                    newVal != null &&
                    (!oldVal || newVal !== oldVal) &&
                    !this.fenceCheckinModified
                ) {
                    this.desiredFenceIntervalValue =
                        this.getIntervalValueFromSeconds(newVal);
                }
            },
        },

        normalCheckinIntervalSeconds: {
            immediate: true,
            handler: function (newVal, oldVal) {
                if (
                    newVal != null &&
                    (!oldVal || newVal !== oldVal) &&
                    !this.normalCheckinModified
                ) {
                    this.desiredNormalIntervalValue =
                        this.getIntervalValueFromSeconds(newVal);
                }
            },
        },

        pendingPowerMode(newVal, oldVal) {
            if (oldVal !== newVal && newVal === this.selectedPowerMode) {
                this.selectedPowerMode = '';
            }
        },

        unitState(newVal, oldVal) {
            if (oldVal !== newVal) {
                // Always prefer live backend/general status updates over local optimistic state.
                this.desiredState = 'ARM_STATE_NONE';

                if (newVal === this.selectedState) {
                    this.selectedState = '';
                }
            }
        },

        powerMode(newVal, oldVal) {
            if (oldVal !== newVal) {
                // Always prefer live backend/general status updates over local optimistic state.
                this.desiredPowerMode = 'POWER_MODE_NONE';

                if (newVal === this.selectedPowerMode) {
                    this.selectedPowerMode = '';
                }
            }
        },

        backendCellularCarrierName(newVal, oldVal) {
            if (oldVal !== newVal) {
                this.selectedCellularCarrier = '';
            }
        },

        usersList: {
            immediate: true,
            handler(val) {
                if (val.length > 0 && this.selectedAddUser.invalid) {
                    this.setSelectedAddUser(val[0].id, val[0].name);
                }
            },
        },
    },

    created() {
        this.desiredState = 'ARM_STATE_NONE';
        this.desiredPowerMode = 'POWER_MODE_NONE';

        // We can count on unit being defined when this component is created because the card is not created without it
        this.managers = this.unit.managers ? this.unit.managers : [];
        this.unitOrganizations = this.unit.organizations
            ? this.unit.organizations.map((o) => ({
                  ...o,
                  organization_id: o.organization_id,
              }))
            : [];

        this.onViewEditConfigurationValidate();
    },

    methods: {
        addManager() {
            if (this.selectedAddUserValidManager) {
                if (this.managers) {
                    this.managers = [...this.managers, this.selectedAddUser];
                } else {
                    this.managers = [this.selectedAddUser];
                }
                this.edits = true;
                this.$emit('commitEdits');
            } else {
                derror('Cannot add user, id not found in users list');
            }
        },

        addOrganization() {
            if (this.selectedAddOrganizationValidManager) {
                if (this.unitOrganizations) {
                    this.unitOrganizations = [
                        ...this.unitOrganizations,
                        this.selectedAddOrganization,
                    ];
                } else {
                    this.unitOrganizations = [this.selectedAddOrganization];
                }
                this.edits = true;
                this.$emit('commitEdits');
            } else {
                derror(
                    'Cannot add organization, id not found in organizations list',
                );
            }
        },

        async deleteManager(manager) {
            if (this.managers) {
                this.managers = this.managers.filter(
                    (m) => m.user_id !== manager.user_id,
                );
            }

            // removes organizations associated with the manager so no orgs are part of unit that don't have manager assigned.
            let managerOrgs = [];
            const orgUser = await getOrganizationUser(manager.user_id);
            if (orgUser.payload) {
                const org = this.organizations.find(
                    (o) => o.id === orgUser.payload.organization_id,
                );
                if (org) {
                    managerOrgs.push({
                        label: org.organization_name,
                        value: org.id,
                    });
                }
            }
            managerOrgs.forEach((o) => {
                this.unitOrganizations = this.unitOrganizations.filter(
                    (uo) => uo.organization_id != o.id,
                );
            });
            this.edits = true;
            this.$emit('commitEdits');
        },

        deleteSelectedOrganization(organization) {
            if (this.unitOrganizations) {
                this.unitOrganizations = this.unitOrganizations.filter(
                    (o) => o.organization_id !== organization.organization_id,
                );
            }
            this.edits = true;
            this.$emit('commitEdits');
        },

        getIntervalLabelFromValue(val) {
            if (val) {
                if (val.unit === 'continuous') {
                    return this.$t('continuous');
                } else {
                    let unit = val.unit;
                    if (val.value > 1) {
                        unit += 's';
                        // In our current translations, these keys don't have a 'Lower' suffix
                        if (unit !== 'weeks' && unit !== 'days') {
                            unit += 'Lower';
                        }
                    }

                    return `${val.value} ${this.$t('timeUnits.' + unit)}`;
                }
            }

            return '';
        },

        getIntervalValueFromSeconds(sec) {
            // Handle special continuous case
            if (sec === 0) {
                return { value: 1, unit: 'continuous' };
            }

            const weeks = sec / (60 * 60 * 24 * 7);
            const days = sec / (60 * 60 * 24);
            let hours = sec / (60 * 60);
            const minutes = sec / 60;

            if (weeks >= 1) {
                return { value: Math.floor(weeks), unit: 'week' };
            } else if (days >= 1) {
                return { value: Math.floor(days), unit: 'day' };
            } else if (hours >= 1) {
                if (hours !== 1.5) {
                    hours = Math.floor(hours);
                }
                return { value: hours, unit: 'hour' };
            } else {
                return { value: Math.floor(minutes), unit: 'minute' };
            }
        },

        onPowerModeChange(value) {
            if (this.desiredPowerMode !== value) {
                this.showPowerModeConfirm = true;
            }
            this.selectedPowerMode = value;
        },

        onShorePowerLatchChange(value) {
            const response = setShorePowerLatch(this.unit.id, value);

            if (response.status == STATUS_ERROR) {
                toastr.error(this.$t('commandSendFailure'));
            } else {
                toastr.success(this.$t('commandSendSuccessful'));
            }
        },

        setPowerModeShutdown() {
            this.onPowerModeChange('POWER_MODE_STANDBY');
        },

        viewEditConfiguration() {
            this.jsonEditFile = this.jsonFile;
            this.showViewEditConfiguration = true;
        },

        onStateChange(value) {
            if (value !== this.desiredState) {
                this.showStateChangeConfirm = true;
            }
            this.selectedState = value;
        },

        async confirmPowerModeChange() {
            this.showPowerModeConfirm = false;

            const response = await setPowerMode(
                this.unit.id,
                this.selectedPowerMode,
            );

            if (response.status == STATUS_ERROR) {
                this.desiredPowerMode = 'POWER_MODE_NONE';
                toastr.error(this.$t('commandSendFailure'));
            } else {
                this.desiredPowerMode = this.selectedPowerMode;
                toastr.success(this.$t('commandSendSuccessful'));
            }
        },

        setSelectedAddUser(userId, username) {
            this.selectedAddUser = {
                user_id: userId,
                name: username,
            };
        },

        setSelectedAddOrganization(id, name) {
            this.selectedAddOrganization = {
                organization_id: id,
                name: name,
            };
        },

        async confirmCellularCarrierChange() {
            if (this.desiredCellularCarrier) {
                const response = setCellularCarrier(
                    this.unit.id,
                    this.desiredCellularCarrier,
                );

                if (response.status == STATUS_ERROR) {
                    toastr.error(this.$t('commandSendFailure'));
                } else {
                    toastr.success(this.$t('commandSendSuccessful'));

                    this.selectedCellularCarrier = this.desiredCellularCarrier;
                }
            }
            this.showCellularCarrierChangeConfirm = false;
        },

        async onViewEditConfigurationSave() {
            if (this.jsonValid) {
                const response = setCanConfig(this.unit.id, this.jsonEditFile);
                if (response.status == STATUS_ERROR) {
                    toastr.error(this.$t('commandSendFailure'));
                    this.statusErrorText = this.$t('saveFailed');
                } else {
                    toastr.success(this.$t('commandSendSuccessful'));
                    this.showViewEditConfiguration = false;
                }

                this.statusErrorText = '';
                return;
            }
            this.statusErrorDisplayText =
                this.$t('saveNotDone') + ' - ' + this.statusErrorText;
        },

        async onViewEditConfigurationValidate() {
            if (this.jsonEditFile) {
                try {
                    const jsonData = JSON.parse(this.jsonEditFile);

                    // wifi-ap-password must be between 15 and 63 characters long
                    if (jsonData.hasOwnProperty('wifi-ap-password')) {
                        if (typeof jsonData['wifi-ap-password'] === 'string') {
                            if (
                                jsonData['wifi-ap-password'].length < 15 ||
                                jsonData['wifi-ap-password'].length > 63
                            ) {
                                throw new Error(
                                    this.$t('invalidJsonAPPassLength'),
                                );
                            }
                        } else {
                            throw new Error(this.$t('invalidjsonaAPPassType'));
                        }
                    }
                    this.jsonValid = true;
                    this.statusErrorText = '';
                } catch (e) {
                    this.jsonValid = false;
                    this.statusErrorText = this.$t('invalidJson') + '\n' + e;
                }
                this.statusErrorDisplayText = this.statusErrorText;
            }
        },

        onFileSelected() {
            const fileInput = this.$refs.file;
            const selectedFiles = fileInput.files;

            // Check if there is at least one file selected
            if (selectedFiles.length > 0) {
                // If more than one file is selected, alert the user and reset the input
                if (selectedFiles.length > 1) {
                    alert(this.$t('selectonefile'));
                    fileInput.value = '';
                }
            }
        },

        uploadFile() {
            const fileInput = this.$refs.file;
            const selectedFile = fileInput.files[0]; // Get the first selected file

            if (selectedFile) {
                const reader = new FileReader();

                // Define a callback function to handle the file reading
                reader.onload = (e) => {
                    this.jsonEditFile = e.target.result; // Set the contents of the file to the data property
                    this.onViewEditConfigurationValidate();
                };

                // Start reading the selected file as text
                reader.readAsText(selectedFile);
            }
        },

        unValidateJSON() {
            this.statusErrorText = this.$t('jsonvalidationreq');
            this.statusErrorDisplayText = this.statusErrorText;
            this.jsonValid = false;
        },

        onCellularCarrierCancel() {
            this.showCellularCarrierChangeConfirm = false;
        },

        onSelectedCellularCarrierChange(
            cellularCarrierId,
            cellularCarrierName,
        ) {
            this.showCellularCarrierChangeConfirm = true;

            // update selected option
            this.desiredCellularCarrier = {
                value: cellularCarrierId,
                name: cellularCarrierName,
            };
        },

        toggleUnitState() {
            // If unit is armed, disarm, otherwise send arm command
            this.showStateChangeConfirm = true;
        },

        async confirmStateChange() {
            if (this.selectedState == 'ARM_STATE_DISARMED') {
                const response = await disarmUnit(this.unit.id);
                if (response.status == STATUS_ERROR) {
                    this.desiredState = 'ARM_STATE_NONE';
                    toastr.error(this.$t('commandSendFailure'));
                } else {
                    this.desiredState = this.selectedState;
                    toastr.success(this.$t('commandSendSuccessful'));
                }
            } else {
                const response = await armUnit(this.unit.id);
                if (response.status == STATUS_ERROR) {
                    this.desiredState = 'ARM_STATE_NONE';
                    toastr.error(this.$t('commandSendFailure'));
                } else {
                    this.desiredState = this.selectedState;
                    toastr.success(this.$t('commandSendSuccessful'));
                }
            }
            this.showStateChangeConfirm = false;
        },

        toggleCollapsible(collapsible) {
            this.collapsiblesOpen[collapsible] =
                !this.collapsiblesOpen[collapsible];
        },

        async saveChanges() {
            const saveStatus = {
                saveFailed: false,
                saveSucceeded: false,
                saveFailText: '',
            };

            // We've updated managers at some point, so we need to make the update call
            if (this.edits) {
                let errorResponse = null;
                const response = await setManagers(this.unit.id, this.managers);

                if (response.status === STATUS_ERROR) {
                    errorResponse = response;
                }

                // now handle organizations if no errors with managers
                if (response.status !== STATUS_ERROR) {
                    const response = await setOrganizations(
                        this.unit.id,
                        this.unitOrganizations,
                    );

                    if (response.status === STATUS_ERROR) {
                        errorResponse = response;
                    }
                }

                if (errorResponse) {
                    if (response.msg) {
                        saveStatus.saveFailText = this.$t(
                            response.msg,
                            response.msg_data,
                        );
                    }
                    saveStatus.saveFailed = true;
                    saveStatus.saveSucceeded = false;
                    return saveStatus;
                } else {
                    saveStatus.saveFailed = false;
                    saveStatus.saveSucceeded = true;
                    saveStatus.saveFailText = '';

                    this.edits = false;
                }
            }

            return saveStatus;
        },

        onNormalCheckinIntervalChanged(value) {
            // value is of form: { value: Number, unit: String }
            this.normalIntervalIntermediateValue = value;
            this.normalCheckinModified = true;
        },

        onFenceCheckinIntervalChanged(value) {
            this.fenceIntervalIntermediateValue = value;
            this.fenceCheckinModified = true;
        },

        onNormalCheckinIntervalSubmit() {
            const desired = this.desiredNormalIntervalValue;
            const intermediate = this.normalIntervalIntermediateValue;

            if (
                desired === null ||
                intermediate.value !== desired.value ||
                intermediate.unit !== desired.unit
            ) {
                this.showNormalIntervalChangeConfirm = true;
            }
        },

        onFenceCheckinIntervalSubmit() {
            const desired = this.desiredFenceIntervalValue;
            const intermediate = this.fenceIntervalIntermediateValue;

            if (
                desired == null ||
                intermediate.value !== desired.value ||
                intermediate.unit !== desired.unit
            ) {
                this.showFenceIntervalChangeConfirm = true;
            }
        },

        onNormalCheckinIntervalCancel() {
            this.showNormalIntervalChangeConfirm = false;
            this.desiredNormalIntervalValue = this.getIntervalValueFromSeconds(
                this.normalCheckinIntervalSeconds,
            );
            this.normalIntervalIntermediateValue = null;
        },

        onFenceCheckinIntervalCancel() {
            this.showFenceIntervalChangeConfirm = false;
            this.desiredFenceIntervalValue = this.getIntervalValueFromSeconds(
                this.fenceCheckinIntervalSeconds,
            );
            this.fenceIntervalIntermediateValue = null;
        },

        getIntervalSecondsFromValue(value) {
            if ((value.value || value.value === 0) && value.unit) {
                switch (value.unit) {
                    case 'minute':
                        return value.value * 60;
                    case 'hour':
                        return value.value * 60 * 60;
                    case 'day':
                        return value.value * 60 * 60 * 24;
                    case 'week':
                        return value.value * 60 * 60 * 24 * 7;
                    case 'continuous':
                        return 0;
                }
            }

            console.error('Did not receive valid unit in slider value');
        },

        async confirmNormalIntervalChange() {
            const seconds = this.getIntervalSecondsFromValue(
                this.normalIntervalIntermediateValue,
            );

            if (seconds != null) {
                const response = await setCheckinTimes(
                    this.unit.id,
                    seconds,
                    null,
                );
                if (response.status == STATUS_ERROR) {
                    toastr.error(this.$t('commandSendFailure'));
                } else {
                    toastr.success(this.$t('commandSendSuccessful'));
                }
            }
            this.desiredNormalIntervalValue =
                this.normalIntervalIntermediateValue;
            this.normalIntervalIntermediateValue = null;
            this.showNormalIntervalChangeConfirm = false;
        },

        async confirmFenceIntervalChange() {
            const seconds = this.getIntervalSecondsFromValue(
                this.fenceIntervalIntermediateValue,
            );

            if (seconds != null) {
                const response = await setCheckinTimes(
                    this.unit.id,
                    null,
                    seconds,
                );
                if (response.status == STATUS_ERROR) {
                    toastr.error(this.$t('commandSendFailure'));
                } else {
                    toastr.success(this.$t('commandSendSuccessful'));
                }
            }
            this.desiredFenceIntervalValue =
                this.fenceIntervalIntermediateValue;
            this.fenceIntervalIntermediateValue = null;
            this.showFenceIntervalChangeConfirm = false;
        },

        openFirmwareSelection() {
            this.firmwareArray = [''];
            // call server to get list of firmware names
            getFirmwareUpdateList().then((firmwarePayloadObject) => {
                const payload = firmwarePayloadObject.payload;
                if (typeof payload === 'object') {
                    // Iterate over the properties of the JSON object
                    for (const key in payload) {
                        if (payload.hasOwnProperty(key)) {
                            const value = payload[key];
                            const valuePathSplitParts = `${value}`.split('/');
                            this.firmwareArray.push({
                                label: valuePathSplitParts[
                                    valuePathSplitParts.length - 1
                                ],
                                value: `${value}`,
                            });
                        }
                    }
                }
            });
            this.showFirmwareSelection = true;
        },

        onFirmwareSelectionCancel() {
            this.showFirmwareSelection = false;
        },

        confirmFirmwareSelection() {
            if (this.selectedFirmwareOption) {
                // call server to get SAS token
                getOTASAS().then((SASResp) => {
                    const sas_token = SASResp.payload;
                    const response = setPendingFirmware(
                        this.unit.id,
                        this.selectedFirmwareOption + '?' + sas_token,
                    );

                    if (response.status == STATUS_ERROR) {
                        toastr.error(this.$t('commandSendFailure'));
                    } else {
                        toastr.success(this.$t('commandSendSuccessful'));
                    }
                });
            }

            this.showFirmwareSelection = false;
        },

        onSelectedFirmwareChange(optionId, optionName) {
            this.selectedFirmwareOption = optionId;
        },

        onSMSWake() {
            this.showSmsWakeConfirm = true;
        },

        confirmSmsWake() {
            const response = sendSMSWake(this.unit.id);

            if (response.status == STATUS_ERROR) {
                toastr.error(this.$t('commandSendFailure'));
            } else {
                toastr.success(this.$t('commandSendSuccessful'));
            }

            this.showSmsWakeConfirm = false;
        },

        onSmsWakeCancel() {
            this.showSmsWakeConfirm = false;
        },

        async CheckAdminOrg(organizationName) {
            if (this.$store.state.currentUser.role == 'organization_admin') {
                const orgUser = await getOrganizationUser(
                    this.$store.state.currentUser.id,
                );
                if (orgUser.payload) {
                    const org = this.organizations.find(
                        (o) => o.id === orgUser.payload.organization_id,
                    );
                    if (org) {
                        return org.organization_name == organizationName;
                    }
                }
            }
            return false;
        },
    },

    components: {
        IntervalSlider: IntervalSlider,
    },
};
</script>

<template>
    <div class="unit-details-card">
        <Collapsible
            :disabled="snapCollapsibles"
            :isOpen="snapCollapsibles ? true : collapsiblesOpen.stateOverrides"
            @toggle="toggleCollapsible('stateOverrides')"
        >
            <template #button>
                <div class="collapsible-block">
                    <div class="center-flex">
                        <span>{{ $t('command_plural') }}</span>
                    </div>
                    <i class="fa fa-caret-down" v-if="!snapCollapsibles"></i>
                </div>
            </template>
            <template #content>
                <div class="status-content">
                    <div class="content-block">
                        <div class="command">
                            <div class="title">{{ $t('unitState') }}</div>
                            <Toggle
                                :leftValue="'ARM_STATE_ARMED'"
                                :rightValue="'ARM_STATE_DISARMED'"
                                :value="armStateToggleValue"
                                :leftLabel="$t('arm')"
                                :rightLabel="$t('disarmed')"
                                :hollow="pendingArmState !== ''"
                                @change="onStateChange($event)"
                            />
                        </div>
                        <div class="command">
                            <div class="title">{{ $t('wifi') }}</div>
                            <Toggle
                                :leftValue="'POWER_MODE_SETUP'"
                                :rightValue="'POWER_MODE_HIGH'"
                                :value="powerModeToggleValue"
                                :leftLabel="$t('enabled')"
                                :rightLabel="$t('disabled')"
                                :hollow="pendingPowerMode !== ''"
                                @change="onPowerModeChange($event)"
                            />
                        </div>
                        <div class="command">
                            <div class="title">{{ $t('shorePowerLatch') }}</div>
                            <Toggle
                                :leftValue="true"
                                :rightValue="false"
                                :value="shorePowerLatch"
                                :leftLabel="$t('enabled')"
                                :rightLabel="$t('disabled')"
                                @change="onShorePowerLatchChange($event)"
                            />
                        </div>
                        <div class="command">
                            <div class="title">{{ $t('checkin') }}</div>
                            <IntervalSlider
                                :value="normalIntervalSliderValue"
                                :sections="intervalSections"
                                :label="selectedNormalIntervalLabel"
                                @change="onNormalCheckinIntervalChanged"
                                @submit="onNormalCheckinIntervalSubmit"
                            />
                        </div>
                        <div class="command">
                            <div class="title">{{ $t('geofenceCheckin') }}</div>
                            <IntervalSlider
                                :value="fenceIntervalSliderValue"
                                :sections="intervalSections"
                                :label="selectedFenceIntervalLabel"
                                @change="onFenceCheckinIntervalChanged"
                                @submit="onFenceCheckinIntervalSubmit"
                            />
                        </div>

                        <div class="command">
                            <div class="title">{{ $t('cellularCarrier') }}</div>

                            <div class="add-container">
                                <div class="control-row">
                                    <DropdownMenu
                                        :options="cellularCarrierOptions"
                                        :maxHeight="'200px'"
                                        outline
                                        @change="
                                            onSelectedCellularCarrierChange
                                        "
                                        :value="cellularCarrierOptionValue"
                                    ></DropdownMenu>
                                </div>
                            </div>
                        </div>

                        <div class="flex-container">
                            <div class="flex-child">
                                <div class="command">
                                    <div class="title">{{ $t('smsWake') }}</div>
                                    <div class="command-button-ctn">
                                        <button
                                            class="command-button"
                                            @click="onSMSWake"
                                        >
                                            {{ $t('send') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div class="flex-child">
                                <div class="command">
                                    <div class="title">
                                        {{ $t('shutdown') }}
                                    </div>
                                    <div class="command-button-ctn">
                                        <button
                                            class="command-button"
                                            @click="setPowerModeShutdown"
                                        >
                                            {{ $t('send') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="flex-container">
                            <div class="flex-child">
                                <div class="command">
                                    <div class="title">
                                        {{ $t('configuration') }}
                                    </div>
                                    <div class="command-button-ctn">
                                        <button
                                            class="command-button"
                                            @click="viewEditConfiguration"
                                        >
                                            {{ $t('viewedit') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div class="flex-child">
                                <div class="command">
                                    <div class="title">
                                        {{ $t('firmwareUpdate') }}
                                    </div>
                                    <div class="command-button-ctn">
                                        <button
                                            class="command-button"
                                            @click="openFirmwareSelection"
                                        >
                                            {{ $t('select') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Power Mode Change Confirmation Dialogue -->
                        <Modal
                            v-if="showPowerModeConfirm"
                            @ok="confirmPowerModeChange"
                            @cancel="showPowerModeConfirm = false"
                        >
                            <template #header
                                ><h3>{{ $t('confirmChange') }}</h3></template
                            >
                            <template #body
                                ><div>
                                    {{
                                        $t('verifyPowerModeChangePrompt', {
                                            new_mode: selectedPowerMode,
                                        })
                                    }}
                                </div></template
                            >
                        </Modal>

                        <!-- Arm State Change Confirmation Dialogue -->
                        <Modal
                            v-if="showStateChangeConfirm"
                            @ok="confirmStateChange"
                            @cancel="showStateChangeConfirm = false"
                        >
                            <template #header
                                ><h3>{{ $t('confirmChange') }}</h3></template
                            >
                            <template #body
                                ><div>
                                    {{
                                        $t('verifyStateChangePrompt', {
                                            new_state: selectedState,
                                        })
                                    }}
                                </div></template
                            >
                        </Modal>

                        <!-- Normal Checkin Interval Change Confirmation Dialogue -->
                        <Modal
                            v-if="showNormalIntervalChangeConfirm"
                            @ok="confirmNormalIntervalChange"
                            @cancel="onNormalCheckinIntervalCancel"
                        >
                            <template #header
                                ><h3>{{ $t('confirmChange') }}</h3></template
                            >
                            <template #body
                                ><div>
                                    {{
                                        $t('verifyNormalIntervalChangePrompt', {
                                            interval:
                                                selectedNormalIntervalLabel,
                                        })
                                    }}
                                </div></template
                            >
                        </Modal>

                        <!-- Fence Checkin Interval Change Confirmation Dialogue -->
                        <Modal
                            v-if="showFenceIntervalChangeConfirm"
                            @ok="confirmFenceIntervalChange"
                            @cancel="onFenceCheckinIntervalCancel"
                        >
                            <template #header
                                ><h3>{{ $t('confirmChange') }}</h3></template
                            >
                            <template #body
                                ><div>
                                    {{
                                        $t('verifyFenceIntervalChangePrompt', {
                                            interval:
                                                selectedFenceIntervalLabel,
                                        })
                                    }}
                                </div></template
                            >
                        </Modal>

                        <!-- Cellular Carrier Checkin Interval Change Confirmation Dialogue -->
                        <Modal
                            v-if="showCellularCarrierChangeConfirm"
                            @ok="confirmCellularCarrierChange"
                            @cancel="onCellularCarrierCancel"
                        >
                            <template #header
                                ><h3>{{ $t('confirmChange') }}</h3></template
                            >
                            <template #body
                                ><div>
                                    {{
                                        $t(
                                            'verifyCellularCarrierChangePrompt',
                                            {
                                                cellular_carrier:
                                                    selectedCellularCarrierLabel,
                                            },
                                        )
                                    }}
                                </div></template
                            >
                        </Modal>

                        <!-- Send SMS Wake Confirmation Dialogue -->
                        <Modal
                            v-if="showSmsWakeConfirm"
                            @ok="confirmSmsWake"
                            @cancel="onSmsWakeCancel"
                        >
                            <template #header
                                ><h3>{{ $t('confirmChange') }}</h3></template
                            >
                            <template #body
                                ><div>
                                    {{ $t('verifySmsWakePrompt') }}
                                </div></template
                            >
                        </Modal>

                        <!-- JSON Confirmation Dialogue -->
                        <Modal
                            v-if="showViewEditConfiguration"
                            @ok="onViewEditConfigurationSave"
                            @validate="onViewEditConfigurationValidate"
                            @cancel="showViewEditConfiguration = false"
                            width="90vw"
                            height="90vh"
                            jsonViewEdit="true"
                        >
                            <template #header
                                ><h3>{{ $t('vieweditjsonconf') }}</h3></template
                            >
                            <template #body>
                                <div>
                                    <div>
                                        {{ $t('selectfileupload') }}
                                        <input
                                            type="file"
                                            @change="onFileSelected"
                                            ref="file"
                                            accept=".json"
                                        />
                                        <button @click="uploadFile">
                                            {{ $t('upload') }}
                                        </button>
                                    </div>
                                    <br />
                                    <textarea
                                        v-model="jsonEditFile"
                                        class="json-textarea"
                                        @input="unValidateJSON"
                                    >
                                    </textarea>
                                    <div class="jsonError">
                                        {{ statusErrorDisplayText }}
                                    </div>
                                </div>
                            </template>
                        </Modal>

                        <Modal
                            v-if="showFirmwareSelection"
                            @ok="confirmFirmwareSelection"
                            @cancel="onFirmwareSelectionCancel"
                        >
                            <template #header
                                ><h3>
                                    {{ $t('selectFirmwareFile') }}
                                </h3></template
                            >
                            <template #body>
                                <div>
                                    <div class="add-container">
                                        <div class="control-row">
                                            <DropdownMenu
                                                :options="firmwareOptions"
                                                :maxHeight="'200px'"
                                                outline
                                                @change="
                                                    onSelectedFirmwareChange
                                                "
                                                class="dropdown-adjustments"
                                                :value="firmwareOptionValue"
                                            ></DropdownMenu>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </Modal>
                    </div>
                </div>
            </template>
        </Collapsible>

        <div style="height: 20px"></div>

        <Collapsible
            :disabled="snapCollapsibles"
            :isOpen="snapCollapsibles ? true : collapsiblesOpen.unitManagers"
            @toggle="toggleCollapsible('unitManagers')"
        >
            <template #button>
                <div class="collapsible-block">
                    <div class="center-flex">
                        <span>{{ $t('unitManagers') }}</span>
                    </div>
                    <i class="fa fa-caret-down" v-if="!snapCollapsibles"></i>
                </div>
            </template>
            <template #content>
                <div class="status-content">
                    <div class="content-block row">
                        <div class="add-container">
                            <div class="control-row">
                                <DropdownMenu
                                    :options="userOptions"
                                    :maxHeight="'200px'"
                                    outline
                                    @change="setSelectedAddUser"
                                    :value="selectedAddUser.user_id"
                                ></DropdownMenu>
                            </div>
                            <div class="control-row">
                                <Button
                                    small
                                    @click="addManager"
                                    :disabled="!selectedAddUserValidManager"
                                >
                                    <i class="fa fa-plus"></i>
                                    {{ $t('manager') }}
                                </Button>
                            </div>

                            <div class="control-row">
                                <DropdownMenu
                                    :options="organizationOptions"
                                    :maxHeight="'200px'"
                                    outline
                                    @change="setSelectedAddOrganization"
                                    :value="
                                        selectedAddOrganization.organization_id
                                    "
                                ></DropdownMenu>
                            </div>
                            <div class="control-row">
                                <Button
                                    small
                                    @click="addOrganization"
                                    :disabled="
                                        !selectedAddOrganizationValidManager
                                    "
                                >
                                    <i class="fa fa-plus"></i>
                                    {{ $t('organization') }}
                                </Button>
                            </div>
                        </div>

                        <div class="users-container">
                            <div class="user-block">
                                <span>{{ $t('manager_plural') }}</span>
                                <div
                                    class="user-row"
                                    v-for="manager in managers"
                                    :key="manager.user_id"
                                >
                                    {{ manager.name }}
                                    <div
                                        class="delete-btn-ctn"
                                        v-if="
                                            isUserAdmin ||
                                            (isOrganizationAdmin &&
                                                !isOrgManager[manager.user_id])
                                        "
                                    >
                                        <button @click="deleteManager(manager)">
                                            <i class="far fa-times-circle"></i>
                                        </button>
                                    </div>
                                </div>

                                <div class="user-block">
                                    <span>{{ $t('organization_plural') }}</span>
                                    <div
                                        class="user-row"
                                        v-for="organization in unitOrganizations"
                                        :key="organization.id"
                                    >
                                        {{ organization.name }}
                                        <div
                                            class="delete-btn-ctn"
                                            v-if="
                                                isUserAdmin ||
                                                (isOrganizationAdmin &&
                                                    !isAdminOrg[
                                                        organization
                                                            .organization_id
                                                    ])
                                            "
                                        >
                                            <button
                                                @click="
                                                    deleteSelectedOrganization(
                                                        organization,
                                                    )
                                                "
                                            >
                                                <i
                                                    class="far fa-times-circle"
                                                ></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </Collapsible>
    </div>
</template>

<style lang="scss" scoped>
@use 'sass:color';
@use '../../../../palette.scss' as *;

.unit-details-container {
    .unit-details-card {
        display: flex;
        flex-direction: column;
        width: 100%;
        color: $theme-dark-grey;
        padding-top: 5px;

        .command {
            display: flex;
            flex-direction: column;
            align-items: center;
            color: $theme-dark-grey;
            margin: 15px 10%;

            .title {
                font-size: 1.2em;
                font-weight: bold;
            }

            .command-button-ctn {
                border: 2px solid $theme-light-blue;
                border-radius: 3px;

                &:hover:not(:active) {
                    border: 2px solid
                        color.adjust($theme-light-blue, $lightness: -5%);
                }

                .command-button {
                    min-width: 100px;
                    padding: 5px 10px;
                    background-color: $theme-light-blue;
                    border-style: none;
                    color: white;

                    &:hover {
                        background-color: color.adjust(
                            $theme-light-blue,
                            $lightness: -5%
                        );
                    }

                    &:focus {
                        outline: none;
                    }

                    &.set,
                    &:active {
                        transform: scale(0.98, 0.96);
                    }
                }
            }
        }

        .flex-container {
            display: flex;
        }

        .flex-child {
            flex: 1;
        }

        .flex-child:first-child {
            margin-right: 20px;
        }

        .collapsible-block {
            display: flex;
            align-items: center;
            justify-content: space-between;
        }

        .status-content {
            padding: 5px 10px;

            div:last-child() {
                border-bottom: none;
            }

            .content-block {
                color: $theme-grey;
                position: relative;

                .add-container {
                    display: flex;
                    flex-flow: column;
                    padding: 10px;

                    .control-row {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        width: 100%;
                        margin: 5px 0;

                        button {
                            min-width: 150px;
                            display: inline;
                        }

                        .dropdown-container {
                            min-width: 180px;
                        }

                        .fa-plus {
                            margin-right: 4px;
                        }

                        .dropdown-adjustments {
                            width: 90%;
                        }
                    }
                }

                .users-container {
                    display: flex;
                    flex-flow: column;
                    margin: 0 15px;
                    width: 100%;
                    min-height: 250px;

                    .user-block {
                        padding: 10px 0;
                        margin-right: 20px;

                        & > span {
                            font-weight: bold;
                        }

                        .user-row {
                            display: flex;
                            align-items: center;
                            text-indent: -5px;
                            padding: 0 30px 0 8px;
                            word-break: break-word;
                            position: relative;
                            width: 100%;

                            &:hover {
                                .delete-btn-ctn {
                                    opacity: 1;
                                }
                            }

                            .delete-btn-ctn {
                                transition: opacity 0.25s;
                                position: absolute;
                                opacity: 0;
                                right: 0;

                                button {
                                    background-color: transparent;
                                    color: $theme-red;
                                    font-size: 1.7em;
                                    border: none;
                                    outline: none;
                                    display: inline;

                                    i {
                                        border-radius: 50%;

                                        &:hover {
                                            background-color: color.adjust(
                                                $theme-red,
                                                $alpha: -0.85
                                            );
                                            color: color.adjust(
                                                $theme-red,
                                                $lightness: -5%
                                            );
                                        }

                                        &:active {
                                            transform: scale(0.97);
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                @media (min-width: 662px) {
                    &.row {
                        display: flex;
                        flex-flow: row nowrap;

                        .add-container {
                            margin: 10px 0;
                            width: 50%;
                            border-right: 1px solid $theme-light-grey;
                        }

                        .users-container {
                            width: 50%;

                            .user-block {
                                margin: 0;
                            }
                        }
                    }
                }
            }
        }

        .json-textarea {
            width: 100%;
            height: calc(100vh - 360px);
            overflow: auto;
            white-space: pre;
            overflow-wrap: normal;
            word-wrap: normal;
        }

        .jsonError {
            color: red;
        }
    }
}

::v-deep(.modal-footer button) {
    margin-right: 10px;
}

::v-deep(.modal-footer button:last-child) {
    margin-right: 0;
}
</style>
